import type { ReactNode } from 'react';
import { GetServerSideProps, NextPage } from 'next';
import { BASE_PATHS, getRandomSampleFromArray } from '@belong/common';
import { PrismicDocument } from '@prismicio/types';
import { kebabCase } from 'es-toolkit/compat';
import { fetchCityService } from 'src/api/geo';
import { fetchHomeownerCountService } from 'src/api/homeowner';
import { fetchHomesService } from 'src/api/homes';
import { fetchCityImageService } from 'src/api/medias';
import {
  CoverageRegionModel,
  UnitListingModel,
  ReviewModel,
  CityMediaFilter,
} from 'src/api/models';
import { fetchRegionsService } from 'src/api/region';
import { fetchReviewsService } from 'src/api/reviews';
import {
  fetchBlogPostsList,
  fetchAllBlogProfiles,
} from 'src/features/cms/prismic';
import { CitiesLandingPage } from 'src/features/homeowners-city/components/cities-landing-page/cities-landing-page';
import type { CitiesLandingPageProps } from 'src/features/homeowners-city/components/cities-landing-page/cities-landing-page';
import { getCitiesLandingPageLayout } from 'src/features/homeowners-city/components/cities-landing-page/cities-landing-page-layout';
import { LandingPageLayoutSwitcher } from 'src/features/homeowners/components/landing-page-layout-switcher/landing-page-layout-switcher';
import { LandingPageSwitcher } from 'src/features/homeowners/components/landing-page-switcher/landing-page-switcher';
import {
  getLandingPageGetStaticProps,
  ServerProps,
} from 'src/features/homeowners/components/landing-page/landing-page';
import { createClient } from 'src/prismicio';
import { ApiError } from 'src/utils/build-api-client';
import { buildServerClient } from 'src/utils/server-client';
import type { InitialZustandState } from 'src/store/region';

type StaticMethods = {
  // The page renders either shape — see the `'cityData' in props` branch below
  // — so its layout is handed either too. `ServerProps` alone was too narrow to
  // read `cityData` off.
  getLayout?: (
    page: ReactNode,
    pageProps: CitiesLandingPageProps | ServerProps,
  ) => ReactNode;
};

type Neighborhood = {
  neighborhood: string;
};

type StaticProps = {
  homeownersCount: number;
  cityData: Record<string, unknown>;
  listings: UnitListingModel[];
  initialZustandState?: InitialZustandState;
  blogPosts: PrismicDocument[];
  coverSrc: string;
  reviews: ReviewModel[];
};

const HomeownersCityLandingPage: NextPage<
  CitiesLandingPageProps | ServerProps
> &
  StaticMethods = (props) => {
  if ('cityData' in props) {
    return <CitiesLandingPage {...props} />;
  }

  return <LandingPageSwitcher {...props} />;
};

HomeownersCityLandingPage.getLayout = (page, props) => {
  if ('cityData' in props) {
    return getCitiesLandingPageLayout(page, props.cityData);
  }

  return <LandingPageLayoutSwitcher page={page} />;
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const { query: params } = ctx;
  const slugParam = params?.cityOrRegionSlug || '';
  // `query` types every segment as `string | string[]` because catch-all routes
  // share the type; this route has a single segment, so the array branch is the
  // same first-element read the `Array.isArray` block used to do inline. Kept as
  // one narrowed `string` so the reads below don't each have to re-narrow.
  const cityOrRegionSlug = Array.isArray(slugParam)
    ? (slugParam[0] ?? '')
    : slugParam;

  try {
    const apiClient = buildServerClient();
    const regions = await fetchRegionsService(apiClient);

    if (
      regions.map((region) => region.nameKebabCase).includes(cityOrRegionSlug)
    ) {
      // It is a Region page
      return getLandingPageGetStaticProps(cityOrRegionSlug)(ctx);
    }

    if (!cityOrRegionSlug.endsWith('-property-management')) {
      return {
        redirect: {
          destination: `${BASE_PATHS.HOMEOWNER}/${cityOrRegionSlug}-property-management`,
          permanent: true,
        },
      };
    }

    const splitSlug = cityOrRegionSlug
      .replace('-property-management', '')
      .split('-');
    // `!` because `split` always yields at least one element, so this `pop`
    // cannot actually return `undefined` here.
    const stateId = splitSlug.pop()!.toUpperCase();
    const city = splitSlug
      .map((name) => name.charAt(0).toUpperCase() + name.slice(1))
      .join(' ');

    let response: any;
    // `!` rather than a default: the `catch` below only returns for a 404, so any
    // other failure leaves this unassigned and falls through to the `props`
    // object at the end of the function. That is pre-existing — Next then fails
    // to serialize the `undefined` — and picking a number here would mask it
    // rather than fix it. See docs/development/typecheck-baseline-followups.md.
    let homeownersCount!: number;
    try {
      [response, homeownersCount] = await Promise.all([
        fetchCityService(city, stateId),
        // TODO: Not sure what to default this too, will ask Tyler.
        fetchHomeownerCountService(apiClient).catch(() => 1000),
      ]);
    } catch (e) {
      if (e instanceof ApiError && e?.status === 404) {
        return {
          notFound: true,
        };
      }
    }

    let listings: UnitListingModel[] = [];
    let blogPosts: PrismicDocument[] = [];
    let coverSrc = '';
    let currentRegion: CoverageRegionModel | null = null;
    let reviews: ReviewModel[] = [];

    try {
      const client = createClient();

      if (response.region) {
        currentRegion =
          regions.find((region) => region.id === response.region.id) ?? null;
      }

      // `!` here and at the `fetchReviewsService` call below: this read is
      // unconditional today, so when the city has no matching region it throws
      // and the bare `catch` at the end of this block swallows it, leaving the
      // initializers above in place. Guarding instead would change which
      // branch runs.
      if (response.city === currentRegion!.region) {
        return {
          redirect: {
            destination: `${BASE_PATHS.HOMEOWNER}/${currentRegion!.nameKebabCase}`,
            permanent: false,
          },
        };
      }

      const [
        { items },
        { url },
        { results: landingPageBlogPosts },
        blogProfiles,
        reviewsResponse,
      ] = await Promise.all([
        fetchHomesService({
          city: [response.city],
          state: [stateId],
          fallbackToBiggerArea: true,
          ...(currentRegion && {
            region: currentRegion.id,
          }),
          pageSize: 9,
        }),
        fetchCityImageService({
          city: response.city,
          state: stateId as CityMediaFilter['state'],
          ...(currentRegion && {
            region: currentRegion.name as CityMediaFilter['region'],
          }),
        }),
        fetchBlogPostsList({
          page: 1,
          client,
          pageProps: { pageSize: 50 },
          tags: ['LP Featured Blog'],
        }),
        fetchAllBlogProfiles(client),
        fetchReviewsService({
          City: response.city,
          Region: currentRegion!.name as any,
          ReviewPage: 'City',
          NumberOfReviews: 6,
        }),
      ]);

      // `??` falls back to the same values these are initialized with above,
      // which is what the surrounding `try`/`catch` already relies on when the
      // requests fail. Assigning the raw `undefined` made Next fail to
      // serialize the props instead.
      listings = items ?? [];
      coverSrc = url ?? '';
      reviews = reviewsResponse;

      // Body is not needed here, so we are stripping it out
      const postsWithAutor = landingPageBlogPosts.map((post) => {
        const author = blogProfiles.find(
          (profile) => profile.uid === post?.data?.author,
        );
        const { body, ...postWithoutBody } = post.data;

        return {
          ...post,
          data: postWithoutBody,
          author,
        };
      });

      blogPosts = getRandomSampleFromArray({
        array: postsWithAutor,
        sampleSize: 6,
      });
    } catch {}

    const cityData = { ...response };

    if (Array.isArray(cityData.neighborhoods)) {
      // Bound to a typed local before sorting: `response` is `any`, so mapping
      // straight off it left the comparator's parameters implicitly `any`.
      const neighborhoodLinks: { displayName: string; href: string }[] =
        cityData.neighborhoods.map((elem: Neighborhood) => {
          return {
            displayName: elem.neighborhood,
            href: `${BASE_PATHS.HOMEOWNER}/${cityOrRegionSlug}/${kebabCase(elem.neighborhood)}`,
          };
        });

      cityData.neighborhoods = neighborhoodLinks.sort((a, b) =>
        a.displayName.localeCompare(b.displayName),
      );
    } else {
      cityData.neighborhoods = [];
    }

    const newProps: StaticProps = {
      cityData,
      homeownersCount,
      listings,
      blogPosts,
      coverSrc,
      reviews,
    };

    if (currentRegion) {
      newProps.initialZustandState = {
        region: currentRegion,
      };
    }

    return { props: newProps };
  } catch (error) {
    console.log('[cityOrRegionSlug] => getStaticProps => error', error);

    throw error;
  }
};

export default HomeownersCityLandingPage;
